import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

class OrderPDF {
  #boxesConfig = {
    title: {
      fontSize: 12,
      lineHeight: 6,
      height: 6,
      yOffset: 4,
    },
    desc: {
      fontSize: 12,
      lineHeight: 6,
      yOffset: 4,
    },
    padding: 2,
    border: {
      color: 160,
      thickness: 0.7,
    },
  };

  #docConfig = {
    padding: 5,
    fixedBoxesHeight: 16,
  };

  #gridConfig = {
    color: [160, 160, 160],
    margins: { top: 5, left: 5, right: 5, bottom: 5 },
  };

  #footerConfig = {
    leftBox: {
      padding: 3,
      height: 21,
      fontSize: 12,
      lineHeight: 6,
      yOffset: 3,
    },
    middleBox: {
      padding: 2,
      height: 13,
      fontSize: 12,
      lineHeight: 6,
      yOffset: 3,
    },
    rightBox: {
      padding: 2,
      height: 13,
      fontSize: 12,
      lineHeight: 6,
      yOffset: 3,
    },
  };

  #doc = null;

  constructor(boxesConfig = null) {
    if (boxesConfig != null) {
      this.#boxesConfig = boxesConfig;
    }
  }

  save(name) {
    this.#doc.save(name);
  }

  generate(content) {
    this.#doc = new jsPDF();

    let currentY = this.#docConfig.padding;

    currentY = this.drawHeader(content, currentY) + this.#docConfig.padding;

    currentY = this.drawAddresses(content, currentY) + this.#docConfig.padding;

    currentY = this.drawTable(content, currentY) + this.#docConfig.padding;

    currentY = this.drawFooter1(content, currentY) + this.#docConfig.padding;

    this.drawFooter2(content, currentY);

    return;
  }

  drawHeader(content, startingY) {
    const pageWidth = this.#doc.internal.pageSize.width;
    const columnWidth = (pageWidth - this.#docConfig.padding * 4) / 3;

    let currentY = startingY;

    const columnHeight =
      this.#docConfig.fixedBoxesHeight * 2 + this.#docConfig.padding;

    if (content.organizationLogoBase64 != null) {
      const {
        width: imgWidth,
        height: imgHeight,
      } = this.#doc.getImageProperties(content.organizationLogoBase64);

      const columnAspect = columnWidth / columnHeight;
      const imageAspect = imgWidth / imgHeight;

      let drawWidth, drawHeight;

      if (imageAspect > columnAspect) {
        drawWidth = columnWidth;
        drawHeight = columnWidth / imageAspect;
      } else {
        drawHeight = columnHeight;
        drawWidth = columnHeight * imageAspect;
      }

      const imgX = this.#docConfig.padding + (columnWidth - drawWidth) / 2;
      const imgY = currentY + (columnHeight - drawHeight) / 2;

      this.#doc.addImage(
        content.organizationLogoBase64,
        'png',
        imgX,
        imgY,
        drawWidth,
        drawHeight
      );
    }

    //Draw top 2 boxes
    this.drawCenteredBox(
      this.#docConfig.padding * 2 + columnWidth,
      currentY,
      columnWidth,
      this.#docConfig.fixedBoxesHeight,
      content.headers.purchaseOrder,
      content.orderNumber
    );
    this.drawCenteredBox(
      this.#docConfig.padding * 3 + columnWidth * 2,
      currentY,
      columnWidth,
      this.#docConfig.fixedBoxesHeight,
      content.headers.orderDate,
      content.createdOn
    );

    currentY += this.#docConfig.fixedBoxesHeight + this.#docConfig.padding;

    //Draw box 3 and 4
    this.drawCenteredBox(
      this.#docConfig.padding * 2 + columnWidth,
      currentY,
      columnWidth,
      this.#docConfig.fixedBoxesHeight,
      content.headers.supplier,
      content.supplierName
    );
    this.drawCenteredBox(
      this.#docConfig.padding * 3 + columnWidth * 2,
      currentY,
      columnWidth,
      this.#docConfig.fixedBoxesHeight,
      content.headers.requiredDate,
      content.deliveryDate
    );

    return startingY + columnHeight;
  }

  drawAddresses(content, startingY) {
    const pageWidth = this.#doc.internal.pageSize.width;
    const columnWidth = (pageWidth - this.#docConfig.padding * 4) / 3;

    //Get multi lines texts arrays
    let dynamicBox1WrappedText = this.calculateTextHeight(
      content.organizationAddress,
      columnWidth
    );
    let dynamicBox2WrappedText = this.calculateTextHeight(
      content.supplierAddress ?? '',
      columnWidth
    );
    let dynamicBox3WrappedText = this.calculateTextHeight(
      content.shippingLocationAddress,
      columnWidth
    );

    //Calculate the height of the longest text
    let dynamicTexts = [
      dynamicBox1WrappedText,
      dynamicBox2WrappedText,
      dynamicBox3WrappedText,
    ];

    let maxHeight =
      Math.max(
        ...dynamicTexts.map((x) => x.length * this.#boxesConfig.desc.lineHeight)
      ) +
      this.#boxesConfig.title.height +
      this.#boxesConfig.padding * 2;

    //Draw dynamic boxes
    let currentY = startingY;

    this.drawCenteredBox(
      this.#docConfig.padding,
      currentY,
      columnWidth,
      maxHeight,
      content.headers.client,
      dynamicBox1WrappedText
    );
    this.drawCenteredBox(
      this.#docConfig.padding * 2 + columnWidth,
      currentY,
      columnWidth,
      maxHeight,
      content.headers.supplierAddress,
      dynamicBox2WrappedText
    );
    this.drawCenteredBox(
      this.#docConfig.padding * 3 + columnWidth * 2,
      currentY,
      columnWidth,
      maxHeight,
      content.headers.deliveryAddress,
      dynamicBox3WrappedText
    );

    return startingY + maxHeight;
  }

  drawTable(content, startingY) {
    const tableHeaders = content.headers.table.map((h) => h.title);

    const tableBody = content.items.map((row) =>
      content.headers.table.map((h) => row[h.key] || '')
    );

    let tableFinalY = 0;

    this.#doc.autoTable({
      startY: startingY,
      head: [tableHeaders],
      body: tableBody,
      theme: 'grid',
      tableWidth: 'auto',
      rowPageBreak: 'avoid',
      margin: this.#gridConfig.margins,
      columnStyles: {
        4: { cellWidth: 31, halign: 'right' },
        5: { cellWidth: 32, halign: 'right' },
      },
      styles: {
        lineColor: this.#gridConfig.color, // Use custom color
        lineWidth: 0.5, // Optional: adjust border width
      },
      headStyles: {
        fillColor: this.#gridConfig.color, // Optional header background color
        4: { halign: 'center' },
        5: { halign: 'right' },
        halign: 'center',
      },
      bodyStyles: {
        lineColor: this.#gridConfig.color, // Ensure body cells have the same border color
      },
      alternateRowStyles: {
        lineColor: this.#gridConfig.color, // Apply to alternate row styles
      },
      didDrawPage: (data) => {
        // Update the final Y position after the table is drawn
        tableFinalY = data.cursor.y;
      },
    });

    return tableFinalY;
  }

  drawFooter1(content, startingY) {
    const pageWidth = this.#doc.internal.pageSize.width;

    const pageHeight = this.#doc.internal.pageSize.height;
    if (
      startingY + this.#docConfig.padding + this.#footerConfig.leftBox.height >
      pageHeight
    ) {
      this.#doc.addPage();
      startingY = this.#docConfig.padding;
    }

    const columnWidth = (pageWidth - this.#docConfig.padding * 4) / 3;

    const verticalTableBody = content.pricesArray;

    let tableFinalY = 0;
    this.#doc.autoTable({
      startY: startingY,
      margin: {
        left: columnWidth * 3 + this.#docConfig.padding * 3 - 63,
      },
      tableWidth: 63,
      body: verticalTableBody,
      theme: 'grid', // Table theme
      styles: {
        lineColor: this.#gridConfig.color,
        lineWidth: 0.5,
      },
      bodyStyles: {
        lineColor: this.#gridConfig.color,
      },
      alternateRowStyles: {
        lineColor: this.#gridConfig.color,
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 31 },
        1: { halign: 'right', cellWidth: 32 },
      },
      didDrawPage: (data) => {
        tableFinalY = data.cursor.y;
      },
    });

    return tableFinalY;
  }

  drawFooter2(content, startingY) {
    const pageWidth = this.#doc.internal.pageSize.width;

    const pageHeight = this.#doc.internal.pageSize.height;
    if (
      startingY +
        this.#docConfig.padding +
        this.#footerConfig.middleBox.height >
      pageHeight
    ) {
      this.#doc.addPage();
      startingY = this.#docConfig.padding;
    }

    const columnWidth = (pageWidth - this.#docConfig.padding * 4) / 3;

    let currentX = this.#docConfig.padding * 2 + columnWidth;
    let currentY = startingY;
    this.#doc.rect(
      currentX,
      currentY,
      columnWidth,
      this.#footerConfig.middleBox.height
    );

    currentX += this.#footerConfig.middleBox.padding;
    currentY +=
      this.#footerConfig.middleBox.padding +
      this.#footerConfig.middleBox.yOffset;

    this.#doc.setFontSize(this.#footerConfig.middleBox.fontSize);
    this.#doc.setFont('helvetica', 'normal');

    this.#doc.text(content.headers.payment + ':', currentX, currentY);
    this.#doc.text(
      content.headers.condition + ':',
      currentX,
      currentY + this.#footerConfig.middleBox.lineHeight
    );

    this.#doc.setFont('helvetica', 'bold');

    let paymentWidth = this.#doc.getTextWidth(content.headers.payment + ':');
    this.#doc.text(
      content.supplierPaymentMethod ?? '',
      currentX + paymentWidth + 1,
      currentY
    );
    this.#doc.text(
      content.supplierPaymentCondition ?? '',
      currentX + paymentWidth + 1,
      currentY + this.#footerConfig.middleBox.lineHeight
    );

    this.#doc.setFont('helvetica', 'normal');

    currentX = this.#docConfig.padding * 3 + columnWidth * 2;
    currentY = startingY;
    this.#doc.rect(
      currentX,
      currentY,
      columnWidth,
      this.#footerConfig.rightBox.height
    );

    currentX += this.#footerConfig.rightBox.padding;
    currentY +=
      this.#footerConfig.rightBox.padding + this.#footerConfig.rightBox.yOffset;

    this.#doc.text(content.headers.doneBy + ':', currentX, currentY);
    let width = this.#doc.getTextWidth(content.headers.doneBy + ':');
    this.#doc.text(content.userName, currentX + width + 1, currentY);

    currentY += this.#footerConfig.rightBox.lineHeight;
    this.#doc.text(content.userEmail ?? '', currentX, currentY);

    return startingY + this.#footerConfig.middleBox.height;
  }

  drawCenteredBox(x, y, width, height, title, content) {
    this.#doc.setDrawColor(this.#boxesConfig.border.color);
    this.#doc.setLineWidth(this.#boxesConfig.border.thickness);

    this.#doc.rect(x, y, width, height);

    //Title
    this.#doc.setFontSize(this.#boxesConfig.title.fontSize);
    this.#doc.setFont('helvetica', 'bold');
    const titleWidth = this.#doc.getTextWidth(title);
    const titleX = x + (width - titleWidth) / 2;
    const titleY =
      y + this.#boxesConfig.padding + this.#boxesConfig.title.yOffset;
    this.#doc.text(title, titleX, titleY);

    //Content
    this.#doc.setFontSize(this.#boxesConfig.desc.fontSize);
    this.#doc.setFont('helvetica', 'normal');
    let descY =
      y +
      this.#boxesConfig.padding +
      this.#boxesConfig.title.height +
      this.#boxesConfig.desc.yOffset;

    if (content.constructor === Array) {
      content.forEach((line, index) => {
        const descWidth = this.#doc.getTextWidth(line);
        const descX = x + (width - descWidth) / 2;
        this.#doc.text(
          line,
          descX,
          descY + index * this.#boxesConfig.desc.lineHeight
        );
      });
    } else {
      const descWidth = this.#doc.getTextWidth(content);
      const descX = x + (width - descWidth) / 2;
      this.#doc.text(content, descX, descY);
    }
  }

  calculateTextHeight(content, boxWidth) {
    this.#doc.setFontSize(this.#boxesConfig.desc.fontSize);
    return this.#doc.splitTextToSize(
      content,
      boxWidth - this.#boxesConfig.padding * 2
    );
  }
}

export default OrderPDF;
